/**
 * Portal Layout
 *
 * Minimal, clean layout wrapper for public portals.
 * Features:
 * - Simple header with branding
 * - Mobile-optimized layout
 * - Security badge footer
 *
 * The <header> here is the page's `banner` landmark. It only maps to that role
 * because it sits OUTSIDE <main> — a <header> nested inside <main> is a generic
 * element, which is why the branded pages render their bar through this slot
 * rather than in their own content (issue #2197).
 */
import { ReactNode } from 'react';
import { Shield } from 'lucide-react';
import { motion } from 'framer-motion';

interface PortalLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
  /** Replaces the default Respectly wordmark inside the layout's <header>. */
  header?: ReactNode;
  /** Overrides the header bar's chrome. Default: 'border-b bg-white'. */
  headerClassName?: string;
}

export function PortalLayout({
  children,
  showHeader = true,
  showFooter = true,
  header,
  headerClassName = 'border-b bg-white',
}: PortalLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      {/* Header - the page's banner landmark */}
      {showHeader && (
        <header className={headerClassName}>
          <div className="container mx-auto flex h-14 items-center px-4">
            {header ?? (
              <div className="flex items-center">
                <img src="/logo-dark.svg" alt="Respectly" className="h-8 w-auto" />
              </div>
            )}
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex flex-1 flex-col">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1"
        >
          {children}
        </motion.div>
      </main>

      {/* Footer */}
      {showFooter && (
        <footer className="border-t bg-white py-4">
          <div className="container mx-auto px-4 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>Secure & encrypted</span>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Powered by Respectly
            </p>
          </div>
        </footer>
      )}
    </div>
  );
}
